'use client';

import dynamic from 'next/dynamic';
import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

export interface MediaPreview {
  src: string;
  isVideo: boolean;
  name?: string;
}

interface ImagePreviewContextProps {
  setImagePreview: (src: string) => void;
  setMediaPreview: (src: string, isVideo: boolean) => void;
  setCarouselPreview: (items: MediaPreview[], startIndex: number) => void;
  closePreview: () => void;
}

const ImagePreviewViewer = dynamic(
  () => import('./image-preview-viewer').then((module) => module.ImagePreviewViewer),
  { ssr: false },
);

const ImagePreviewContext = createContext<ImagePreviewContextProps | undefined>(undefined);

export const useImagePreviewContext = (): ImagePreviewContextProps => {
  const context = useContext(ImagePreviewContext);
  if (!context) {
    throw new Error('useImagePreviewContext must be used within an ImagePreviewProvider');
  }
  return context;
};

interface ImagePreviewProviderProps {
  children: ReactNode;
}

export const ImagePreviewProvider: React.FC<ImagePreviewProviderProps> = ({ children }) => {
  const [mediaList, setMediaList] = useState<MediaPreview[] | null>(null);
  const [initialIndex, setInitialIndex] = useState(0);

  const setCarouselPreview = useCallback((items: MediaPreview[], startIndex: number) => {
    setMediaList(items);
    setInitialIndex(startIndex);
  }, []);

  const setImagePreview = useCallback(
    (src: string) => {
      setCarouselPreview([{ src, isVideo: false }], 0);
    },
    [setCarouselPreview],
  );

  const setMediaPreview = useCallback(
    (src: string, isVideo: boolean) => {
      setCarouselPreview([{ src, isVideo }], 0);
    },
    [setCarouselPreview],
  );

  const closePreview = useCallback(() => {
    setMediaList(null);
  }, []);

  useEffect(() => {
    if (!mediaList?.length) return;

    document.body.classList.add('no-scroll');
    return () => {
      document.body.classList.remove('no-scroll');
    };
  }, [mediaList]);

  useEffect(() => {
    if (!mediaList?.length) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closePreview();
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [mediaList, closePreview]);

  return (
    <ImagePreviewContext.Provider
      value={{ setImagePreview, setMediaPreview, setCarouselPreview, closePreview }}>
      {children}
      {mediaList?.length ? (
        <ImagePreviewViewer
          mediaList={mediaList}
          initialIndex={initialIndex}
          closePreview={closePreview}
        />
      ) : null}
    </ImagePreviewContext.Provider>
  );
};
